import { useState } from "react";
import { cn } from "~/lib/utils";
import { resolveTimelineMinimapPreview } from "./timelineMinimapItems";
import type { TimelineTurnNavigationProps } from "./TimelineTurnNavigation";
import { TurnStep } from "./TimelineTurnStep";

/** Upstream's hover rail, with stable identities for previews while history grows. */
export function TimelineTurnRail(
  props: TimelineTurnNavigationProps & { readonly currentIndex: number },
) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const activeIndex = props.items.findIndex((item) => item.id === activeId);
  const index = activeIndex >= 0 ? activeIndex : props.currentIndex;
  const preview = resolveTimelineMinimapPreview(props.items[index] ?? null);
  const top = `${(index / (props.items.length - 1)) * 100}%`;
  const indexFromPointer = (element: HTMLElement, y: number) => {
    const rect = element.getBoundingClientRect();
    return Math.max(
      0,
      Math.min(
        props.items.length - 1,
        Math.round(((y - rect.top) / Math.max(1, rect.height)) * (props.items.length - 1)),
      ),
    );
  };
  return (
    <div
      className="pointer-events-none absolute inset-y-0 start-3 z-20 flex items-center"
      data-testid="timeline-minimap"
    >
      <div className="pointer-events-auto flex flex-col items-center">
        <TurnStep direction="previous" {...props} />
        <div className="relative">
          <div
            role="slider"
            tabIndex={0}
            aria-label="Conversation turns"
            aria-valuemin={1}
            aria-valuemax={props.items.length}
            aria-valuenow={index + 1}
            aria-valuetext={preview?.userText ?? "User message"}
            aria-orientation="vertical"
            className="relative w-8 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{
              height: `min(${Math.max(32, (props.items.length - 1) * 8)}px, calc(100dvh - 18rem))`,
            }}
            onFocus={() => setActiveId(props.items[props.currentIndex]?.id ?? null)}
            onBlur={() => setActiveId(null)}
            onMouseLeave={() => setActiveId(null)}
            onMouseMove={(event) =>
              setActiveId(
                props.items[indexFromPointer(event.currentTarget, event.clientY)]?.id ?? null,
              )
            }
            onClick={(event) => {
              const item = props.items[indexFromPointer(event.currentTarget, event.clientY)];
              if (item) props.onSelect(item);
            }}
            onKeyDown={(event) => {
              const targets: Readonly<Record<string, number>> = {
                ArrowUp: index - 1,
                ArrowDown: index + 1,
                Home: 0,
                End: props.items.length - 1,
              };
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                const item = props.items[index];
                if (item) props.onSelect(item);
                return;
              }
              const target = targets[event.key];
              if (target === undefined) return;
              event.preventDefault();
              setActiveId(
                props.items[Math.max(0, Math.min(props.items.length - 1, target))]?.id ?? null,
              );
            }}
          >
            {props.items.map((item, itemIndex) => (
              <span
                key={item.id}
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute start-1/2 h-0.5 -translate-x-1/2 rounded-full transition-[width,transform] duration-150 motion-reduce:transition-none",
                  item.id === props.currentId ? "bg-foreground" : "bg-muted-foreground/40",
                  itemIndex === index && activeId !== null ? "w-6" : "w-2",
                )}
                style={{ top: `${(itemIndex / (props.items.length - 1)) * 100}%` }}
              />
            ))}
          </div>
          {activeId !== null && preview ? (
            <div
              className="pointer-events-none absolute start-10 w-72 rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg"
              style={{
                top,
                transform:
                  index === 0
                    ? "none"
                    : index === props.items.length - 1
                      ? "translateY(-100%)"
                      : "translateY(-50%)",
              }}
            >
              <p className="truncate font-medium text-sm">{preview.userText ?? "User message"}</p>
              {preview.assistantText ? (
                <p className="mt-1 line-clamp-3 text-muted-foreground text-sm">
                  {preview.assistantText}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <TurnStep direction="next" {...props} />
      </div>
    </div>
  );
}
